import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock3,
  Eye,
  EyeOff,
  Heart,
  KeyRound,
  LockKeyhole,
  Mail,
  MessageCircleHeart,
  Phone,
  Quote,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, errorMessage, fieldError } from '../api/client';
import type { PublicWedding, StaffRole } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button, Field, Notice } from '../components/ui';
import {
  type CountryDialOption,
  DEFAULT_COUNTRY_OPTIONS,
  loadCountryDialOptions,
} from '../lib/countries';
import { WeddingSlideshow, type Slide } from '../components/WeddingSlideshow';

const STORY = [
  'Nous avons cru, dès le premier jour, que la vie avait un plan pour nous.',
  "Nous sommes certains que rien n'arrive par hasard et que les choses prennent toujours tout leur sens lorsque l'on regarde en arrière.",
  'Nous ne nous sommes pas rencontrés trop tard ; nous nous sommes simplement rencontrés au moment parfait, selon le calendrier de Dieu.',
  "Aujourd'hui, nous décidons d'écrire la suite de notre vie ensemble, avec pour bouclier la foi que nous avons en ce Dieu qui nous unit.",
];

const STORY_SLIDES: Slide[] = [
  {
    src: '/media/couple-13-1000.jpg',
    srcSet: '/media/couple-13-480.webp 480w, /media/couple-13-720.webp 720w, /media/couple-13-1000.webp 1000w',
    position: '50% 38%',
  },
  {
    src: '/media/couple-05-1080.jpg',
    srcSet: '/media/couple-05-480.webp 480w, /media/couple-05-720.webp 720w, /media/couple-05-1080.webp 1080w',
    position: '50% 40%',
  },
  {
    src: '/media/couple-01-614.jpg',
    srcSet: '/media/couple-01-480.webp 480w, /media/couple-01-614.webp 614w',
    position: '50% 35%',
  },
  {
    src: '/media/couple-04-810.jpg',
    srcSet: '/media/couple-04-480.webp 480w, /media/couple-04-720.webp 720w, /media/couple-04-810.webp 810w',
    position: '50% 38%',
  },
  {
    src: '/media/couple-11-720.jpg',
    srcSet: '/media/couple-11-480.webp 480w, /media/couple-11-720.webp 720w',
    position: '50% 40%',
  },
];

const GUEST_AUTH_SLIDES: Slide[] = [
  {
    src: '/media/couple-14-720.jpg',
    srcSet: '/media/couple-14-480.webp 480w, /media/couple-14-720.webp 720w',
    position: '50% 30%',
  },
  {
    src: '/media/couple-09-1080.jpg',
    srcSet: '/media/couple-09-480.webp 480w, /media/couple-09-720.webp 720w, /media/couple-09-1080.webp 1080w',
    position: '50% 42%',
  },
  {
    src: '/media/couple-02-720.jpg',
    srcSet: '/media/couple-02-480.webp 480w, /media/couple-02-720.webp 720w',
    position: '50% 36%',
  },
  {
    src: '/media/couple-07-1080.jpg',
    srcSet: '/media/couple-07-480.webp 480w, /media/couple-07-720.webp 720w, /media/couple-07-1080.webp 1080w',
    position: '50% 40%',
  },
];

function namesFromWedding(wedding?: PublicWedding) {
  if (wedding?.partners?.length === 2)
    return { first: wedding.partners[0], second: wedding.partners[1] };
  if (wedding?.partnerOneName && wedding.partnerTwoName) {
    return { first: wedding.partnerOneName, second: wedding.partnerTwoName };
  }
  const parts = wedding?.coupleNames?.split(/\s*(?:&|et)\s*/i);
  return { first: parts?.[0] || 'Orry', second: parts?.[1] || 'Rudy' };
}

const HERO_VIDEO_MS = 40000;
const HERO_PHOTO_MS = 8000;
// Événements qui accordent une « activation utilisateur » et autorisent donc à
// rétablir le son (couvre souris, tactile et clavier sur tous les navigateurs).
const HERO_SOUND_GESTURES = [
  'pointerdown',
  'pointerup',
  'mousedown',
  'touchend',
  'keydown',
  'click',
] as const;

export function HomePage() {
  const [wedding, setWedding] = useState<PublicWedding>();
  const [unavailable, setUnavailable] = useState(false);
  const [heroSlide, setHeroSlide] = useState<'video' | 'photo'>('video');
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const heroSlideRef = useRef<'video' | 'photo'>('video');
  // Vrai dès que la vidéo joue réellement avec le son (sert au carrousel pour
  // ne pas hacher la bande-son).
  const heroSoundLiveRef = useRef(false);

  useEffect(() => {
    heroSlideRef.current = heroSlide;
  }, [heroSlide]);

  useEffect(() => {
    let active = true;
    api
      .publicWedding()
      .then((data) => {
        if (active) setWedding(data);
      })
      .catch(() => {
        if (active) setUnavailable(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // Démarrage de la vidéo du hero avec le son, autant que le navigateur
  // l'autorise. Aucun bouton. Les navigateurs bloquent l'autoplay sonore tant
  // que l'internaute n'a pas « activé » la page. Stratégie :
  //   1. lecture muette garantie au chargement ;
  //   2. dès le premier geste (clic, appui, relâché tactile, touche) OU dès que
  //      `navigator.userActivation` signale une interaction, on rétablit le son ;
  //   3. re-tentative au retour sur l'onglet.
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    video.volume = 0.6;

    let done = false;
    let poll = 0;

    const play = (muted: boolean) => {
      video.muted = muted;
      const attempt = video.play();
      return attempt && typeof attempt.then === 'function'
        ? attempt
        : Promise.resolve();
    };

    const onGesture = () => tryUnmute();

    const tryUnmute = () => {
      if (done) return;
      play(false)
        .then(() => {
          if (!video.muted && !video.paused) finish();
        })
        .catch(() => {
          // Refusé : on garde une lecture muette et on attend le prochain geste.
          play(true).catch(() => {});
        });
    };

    const finish = () => {
      done = true;
      heroSoundLiveRef.current = !video.muted && !video.paused;
      teardown();
    };

    const teardown = () => {
      for (const type of HERO_SOUND_GESTURES) {
        window.removeEventListener(type, onGesture, true);
      }
      window.clearInterval(poll);
    };

    // 1) Lecture muette immédiate (toujours autorisée).
    play(true).catch(() => {});

    // 2) Rattrapage au premier geste.
    for (const type of HERO_SOUND_GESTURES) {
      window.addEventListener(type, onGesture, { capture: true, passive: true });
    }

    // 2 bis) Filet de sécurité : si l'internaute a interagi via un événement non
    // couvert ci-dessus, `navigator.userActivation.hasBeenActive` le révèle.
    poll = window.setInterval(() => {
      if (done) {
        window.clearInterval(poll);
        return;
      }
      if (video.muted && navigator.userActivation?.hasBeenActive) tryUnmute();
    }, 1000);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (video.paused) play(video.muted).catch(() => {});
      if (!done) tryUnmute();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      done = true;
      teardown();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Carrousel du hero : la vidéo, puis la photo (contenu complet).
  useEffect(() => {
    const video = heroVideoRef.current;
    if (video) {
      if (heroSlide === 'video') {
        // Sans le son, on rembobine à chaque passage ; avec le son, on laisse
        // la vidéo courir pour garder une bande-son continue.
        if (!heroSoundLiveRef.current) {
          try {
            video.currentTime = 0;
          } catch {
            // certains navigateurs refusent tant que les métadonnées ne sont pas prêtes
          }
        }
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      } else if (!heroSoundLiveRef.current) {
        video.pause();
      }
    }
    const delay = heroSlide === 'video' ? HERO_VIDEO_MS : HERO_PHOTO_MS;
    const timer = window.setTimeout(
      () => setHeroSlide((current) => (current === 'video' ? 'photo' : 'video')),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [heroSlide]);

  const names = namesFromWedding(wedding);

  return (
    <main id="contenu">
      <section className={`home-hero home-hero--${heroSlide}`}>
        <div className="home-hero__photo" aria-hidden="true">
          <video
            ref={heroVideoRef}
            className={`home-hero__video ${heroSlide === 'video' ? 'is-active' : ''}`}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disableRemotePlayback
            tabIndex={-1}
            poster="/media/hero-couple-portrait-720.jpg"
          >
            <source src="/media/home-hero-sound.mp4" type="video/mp4" />
          </video>
          <picture className={`home-hero__still ${heroSlide === 'photo' ? 'is-active' : ''}`}>
            <source
              type="image/webp"
              srcSet="/media/couple-14-480.webp 480w, /media/couple-14-720.webp 720w"
              sizes="100vw"
            />
            <img
              src="/media/couple-14-720.jpg"
              srcSet="/media/couple-14-480.jpg 480w, /media/couple-14-720.jpg 720w"
              sizes="100vw"
              alt=""
              loading="lazy"
              decoding="async"
            />
          </picture>
          <div className="home-hero__veil" />
        </div>
        <div className="home-hero__content">
          <div className="home-hero__reveal">
            <div className="home-hero__ornament">
              <span /> <Heart fill="currentColor" aria-hidden="true" /> <span />
            </div>
            <p className="eyebrow">Nous allons nous marier</p>
            <h1>
              <span>{names.first}</span>
              <i>&amp;</i>
              <span>{names.second}</span>
            </h1>
            <p className="home-hero__lead">Deux âmes, une foi, une promesse pour la vie.</p>
          </div>
          <Link className="button button--light" to="/connexion">
            Consulter mon invitation <ArrowRight aria-hidden="true" />
          </Link>
          {unavailable && (
            <p className="home-hero__service-note">
              Certaines informations sont momentanément indisponibles.
            </p>
          )}
        </div>
        <a className="scroll-cue" href="#notre-histoire" aria-label="Découvrir notre histoire">
          <span>Découvrir</span>
          <i />
        </a>
      </section>

      <section className="story-section" id="notre-histoire">
        <div className="story-section__collage" aria-label="true">
          <figure className="story-photo story-photo--main">
            <WeddingSlideshow slides={STORY_SLIDES} />
          </figure>
          <span className="story-section__seal">
            <Heart aria-hidden="true" />
            <i>O &amp; R</i>
          </span>
        </div>
        <article className="story-section__copy">
          <p className="eyebrow">Notre histoire d'amour</p>
          <h2>
            Le moment parfait,
            <br />
            selon le calendrier de Dieu.
          </h2>
          <Quote className="story-section__quote-icon" aria-hidden="true" />
          {(wedding?.story ? wedding.story.split(/\n\s*\n/) : STORY).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <p className="story-section__signature">L'amour existe, et nous le vivons !</p>
        </article>
      </section>

      <section className="invitation-callout invitation-callout--plain">
        <div className="invitation-callout__content">
          <Sparkles aria-hidden="true" />
          <p className="eyebrow">Vous êtes des nôtres ?</p>
          <h2>Votre invitation vous attend.</h2>
          <p>
            Entrez le numéro de téléphone associé à votre invitation pour découvrir les célébrations
            auxquelles vous êtes convié.
          </p>
          <Link className="button button--primary" to="/connexion">
            Ouvrir mon invitation <ArrowRight aria-hidden="true" />
          </Link>
          <small>
            <LockKeyhole aria-hidden="true" /> Accès personnel et sécurisé
          </small>
        </div>
      </section>
    </main>
  );
}

interface StoredChallenge {
  challengeId: string;
  countryCode: string;
  phone: string;
  expiresAt: number;
  resendAt: number;
  returnTo?: string;
}

const CHALLENGE_KEY = 'orry-rudy.guest-challenge';

function readChallenge(): StoredChallenge | null {
  try {
    const value = sessionStorage.getItem(CHALLENGE_KEY);
    return value ? (JSON.parse(value) as StoredChallenge) : null;
  } catch {
    return null;
  }
}

function AuthVisual({
  media = 'couple-14',
  width = 720,
  slides,
}: {
  media?: string;
  width?: number;
  slides?: Slide[];
}) {
  return (
    <aside className="auth-visual" aria-hidden="true">
      {slides ? (
        <WeddingSlideshow slides={slides} />
      ) : (
        <picture>
          <source srcSet={`/media/${media}-${width}.webp`} type="image/webp" />
          <img src={`/media/${media}-${width}.jpg`} alt="" />
        </picture>
      )}
      <div className="auth-visual__veil" />
      <div className="auth-visual__quote">
        <Heart fill="currentColor" />
        <p>
          « L'amour existe,
          <br />
          et nous le vivons. »
        </p>
        <span>Orry &amp; Rudy</span>
      </div>
    </aside>
  );
}

export function GuestLoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState('+243');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const [countryOptions, setCountryOptions] =
    useState<CountryDialOption[]>(DEFAULT_COUNTRY_OPTIONS);

  useEffect(() => {
    let active = true;
    void loadCountryDialOptions().then((options) => {
      if (active) setCountryOptions(options);
    });
    return () => {
      active = false;
    };
  }, []);

  if (auth.session?.principal.type === 'guest') return <Navigate replace to="/mon-espace" />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(undefined);
    try {
      const session = await api.loginGuestByPhone(countryCode, phone.trim());
      auth.acceptSession(session);
      const returnTo = (location.state as { from?: string } | null)?.from;
      navigate(returnTo?.startsWith('/mes-invitations/') ? returnTo : '/mon-espace', {
        replace: true,
      });
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="contenu" className="auth-page">
      <AuthVisual slides={GUEST_AUTH_SLIDES} />
      <section className="auth-panel">
        <div className="auth-panel__inner">
          <Link className="back-link" to="/">
            <ArrowLeft aria-hidden="true" /> Retour à l'accueil
          </Link>
          <div className="auth-panel__icon">
            <MessageCircleHeart aria-hidden="true" />
          </div>
          <p className="eyebrow">Votre espace personnel</p>
          <h1>Retrouvez votre invitation</h1>
          <p className="auth-panel__intro">
            Renseignez le numéro associé à votre invitation pour y accéder directement.
          </p>

          {Boolean(error) && <Notice tone="error">{errorMessage(error)}</Notice>}
          <form className="auth-form" onSubmit={submit} noValidate>
            <Field
              label="Numéro de téléphone"
              required
              error={fieldError(error, 'phone')}
              hint="Sélectionnez le pays si vous utilisez un format national."
            >
              <div className="phone-field">
                <select
                  className="select-country"
                  aria-label="Indicatif pays"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                >
                  {countryOptions.map((option) => (
                    <option key={option.iso} value={option.dialCode}>
                      {option.name} {option.dialCode}
                    </option>
                  ))}
                </select>
                <div className="input-with-icon">
                  <Phone aria-hidden="true" />
                  <input
                    aria-label="Téléphone"
                    type="tel"
                    autoComplete="tel-national"
                    inputMode="tel"
                    placeholder="81 234 56 78"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    required
                  />
                </div>
              </div>
            </Field>
            <Button type="submit" loading={loading} disabled={!phone.trim()}>
              Ouvrir mon invitation <ArrowRight aria-hidden="true" />
            </Button>
          </form>
          <p className="auth-panel__privacy">
            <ShieldCheck aria-hidden="true" /> Votre numéro est utilisé uniquement pour retrouver
            votre invitation. <Link to="/confidentialite">En savoir plus</Link>
          </p>
        </div>
      </section>
    </main>
  );
}

function formatCountdown(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

export function VerificationPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<StoredChallenge | null>(() => readChallenge());
  const [code, setCode] = useState('');
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<unknown>();
  const [resent, setResent] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (!challenge) return <Navigate replace to="/connexion" />;
  const remaining = Math.max(0, Math.ceil((challenge.expiresAt - now) / 1000));
  const resendRemaining = Math.max(0, Math.ceil((challenge.resendAt - now) / 1000));
  const masked = challenge.phone.length > 4 ? `•••• ${challenge.phone.slice(-4)}` : challenge.phone;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(undefined);
    try {
      const session = await api.verifyGuestCode(challenge.challengeId, code);
      auth.acceptSession(session);
      sessionStorage.removeItem(CHALLENGE_KEY);
      navigate(
        challenge.returnTo?.startsWith('/mes-invitations/') ? challenge.returnTo : '/mon-espace',
        { replace: true },
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resendRemaining) return;
    setResending(true);
    setError(undefined);
    setResent(false);
    try {
      const response = await api.requestGuestCode(challenge.countryCode, challenge.phone);
      const next = {
        ...challenge,
        challengeId: response.challengeId,
        expiresAt: Date.now() + (response.expiresIn || 300) * 1000,
        resendAt: Date.now() + (response.resendAfter || 60) * 1000,
      };
      setChallenge(next);
      sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(next));
      setCode('');
      setResent(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setResending(false);
    }
  };

  return (
    <main id="contenu" className="auth-page">
      <AuthVisual slides={GUEST_AUTH_SLIDES} />
      <section className="auth-panel">
        <div className="auth-panel__inner">
          <Link className="back-link" to="/connexion">
            <ArrowLeft aria-hidden="true" /> Modifier le numéro
          </Link>
          <div className="auth-panel__icon">
            <KeyRound aria-hidden="true" />
          </div>
          <p className="eyebrow">Vérification</p>
          <h1>Entrez votre code</h1>
          <p className="auth-panel__intro">
            Si ce numéro correspond à une invitation, un code à 6 chiffres a été envoyé au{' '}
            {challenge.countryCode} {masked}.
          </p>
          {resent && <Notice tone="success">Un nouveau code a été demandé.</Notice>}
          {Boolean(error) && <Notice tone="error">{errorMessage(error)}</Notice>}
          <form className="auth-form" onSubmit={submit}>
            <Field label="Code de vérification" required error={fieldError(error, 'code')}>
              <input
                className="otp-input"
                aria-label="Code de vérification à 6 chiffres"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
              />
            </Field>
            <div className="code-timer" aria-live="polite">
              <Clock3 aria-hidden="true" />{' '}
              {remaining ? `Code valable encore ${formatCountdown(remaining)}` : 'Ce code a expiré'}
            </div>
            <Button type="submit" loading={loading} disabled={code.length !== 6 || remaining === 0}>
              Vérifier et ouvrir <ArrowRight aria-hidden="true" />
            </Button>
            <button
              className="text-button"
              type="button"
              disabled={resending || resendRemaining > 0}
              onClick={() => void resend()}
            >
              {resending
                ? 'Nouvelle demande…'
                : resendRemaining
                  ? `Renvoyer dans ${resendRemaining} s`
                  : 'Renvoyer le code'}
            </button>
          </form>
          <p className="auth-help">
            Vous ne recevez rien ? Vérifiez le numéro, attendez une minute, puis réessayez. Si le
            problème persiste, contactez l'organisateur.
          </p>
        </div>
      </section>
    </main>
  );
}

export function StaffLoginPage({ role }: { role: StaffRole }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const [recovery, setRecovery] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);

  const destination = role === 'admin' ? '/admin' : '/accueil/ceremonies';
  if (auth.session?.principal.type === 'staff' && auth.session.principal.roles.includes(role)) {
    return <Navigate replace to={destination} />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(undefined);
    try {
      const session = await api.staffLogin(email.trim(), password);
      if (!session.principal.roles.includes(role))
        throw new Error(
          role === 'admin'
            ? "Ce compte n'a pas accès à l'administration."
            : "Ce compte n'est affecté à aucun accueil.",
        );
      auth.acceptSession(session);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from?.startsWith(role === 'admin' ? '/admin' : '/accueil') ? from : destination, {
        replace: true,
      });
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  };

  const recover = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(undefined);
    try {
      await api.staffRecovery(email.trim());
      setRecoverySent(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="contenu" className={`staff-login staff-login--${role}`}>
      <AuthVisual
        media={role === 'admin' ? 'couple-16' : 'couple-12'}
        width={role === 'admin' ? 720 : 1000}
      />
      <section className="auth-panel">
        <div className="auth-panel__inner">
          <Link className="back-link" to="/">
            <ArrowLeft aria-hidden="true" /> Retour au site
          </Link>
          <div className="auth-panel__icon">
            {role === 'admin' ? <ShieldCheck /> : <KeyRound />}
          </div>
          <p className="eyebrow">{role === 'admin' ? 'Espace des mariés' : "Équipe d'accueil"}</p>
          <h1>{recovery ? 'Retrouver votre accès' : 'Bienvenue'}</h1>
          <p className="auth-panel__intro">
            {recovery
              ? 'Saisissez votre adresse. Si un compte correspond, les instructions de récupération seront envoyées.'
              : role === 'admin'
                ? 'Connectez-vous pour préparer le mariage et suivre les admissions.'
                : 'Connectez-vous puis choisissez la cérémonie à contrôler.'}
          </p>
          {Boolean(error) && <Notice tone="error">{errorMessage(error)}</Notice>}
          {recoverySent && (
            <Notice tone="success">
              Si un compte correspond à cette adresse, un message vient d'être envoyé.
            </Notice>
          )}
          <form className="auth-form" onSubmit={recovery ? recover : submit}>
            <Field label="Adresse e-mail" required error={fieldError(error, 'email')}>
              <div className="input-with-icon">
                <Mail aria-hidden="true" />
                <input
                  aria-label="Adresse e-mail"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
            </Field>
            {!recovery && (
              <Field label="Mot de passe" required error={fieldError(error, 'password')}>
                <div className="input-with-icon input-with-icon--action">
                  <LockKeyhole aria-hidden="true" />
                  <input
                    aria-label="Mot de passe"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={
                      showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                    }
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </Field>
            )}
            <Button
              type="submit"
              loading={loading}
              disabled={!email.trim() || (!recovery && !password)}
            >
              {' '}
              {recovery ? 'Envoyer les instructions' : 'Se connecter'}{' '}
              <ArrowRight aria-hidden="true" />
            </Button>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setRecovery((value) => !value);
                setRecoverySent(false);
                setError(undefined);
              }}
            >
              {recovery ? 'Revenir à la connexion' : 'Mot de passe oublié ?'}
            </button>
          </form>
          <p className="auth-panel__privacy">
            <ShieldCheck aria-hidden="true" /> Connexion nominative. Ne partagez jamais vos
            identifiants.
          </p>
        </div>
      </section>
    </main>
  );
}

export function PrivacyPage() {
  return (
    <main id="contenu" className="legal-page">
      <header className="legal-hero">
        <p className="eyebrow">Vos données, avec délicatesse</p>
        <h1>Confidentialité</h1>
        <p>
          Cette notice explique les données utilisées pour vous remettre une invitation et sécuriser
          l'accès aux cérémonies.
        </p>
      </header>
      <article className="legal-content">
        <nav aria-label="Sommaire">
          <strong>Sur cette page</strong>
          <a href="#collecte">Données collectées</a>
          <a href="#usage">Utilisation</a>
          <a href="#conservation">Conservation</a>
          <a href="#droits">Vos droits</a>
        </nav>
        <div>
          <section id="collecte">
            <h2>Les données collectées</h2>
            <p>
              Nous traitons les informations nécessaires à l'invitation : nom ou libellé du groupe,
              personne de contact, numéro de téléphone, taille du groupe, cérémonies autorisées,
              quotas, placement de table et votre confirmation de présence. Lors du contrôle, nous
              enregistrons la cérémonie, l'heure, le nombre de personnes admises et l'agent ayant
              confirmé l'entrée.
            </p>
          </section>
          <section id="usage">
            <h2>Pourquoi nous les utilisons</h2>
            <p>
              Ces données permettent de vérifier votre numéro, afficher uniquement vos invitations,
              générer vos PDF, puis protéger et tracer les admissions. À l'accueil, l'agent vous
              retrouve par votre nom ou votre numéro : la décision d'admission revient toujours au
              serveur et à l'agent.
            </p>
            <div className="legal-note">
              <ShieldCheck />
              <p>
                <strong>Principe de minimisation</strong>Les agents d'accueil ne voient que les
                informations utiles à la cérémonie qu'ils contrôlent.
              </p>
            </div>
          </section>
          <section id="conservation">
            <h2>Durées de conservation</h2>
            <p>
              Les codes de connexion expirent en quelques minutes et les challenges techniques sont
              supprimés rapidement. À titre de politique de travail, les vérifications d'accueil
              peuvent être gardées 24 heures, les journaux techniques 30 jours, et les admissions ainsi que
              leur audit jusqu'à 12 mois après la dernière cérémonie. Les durées définitives
              relèvent des organisateurs et des obligations applicables.
            </p>
          </section>
          <section id="securite">
            <h2>Sécurité</h2>
            <p>
              Les sessions utilisent des cookies protégés, les numéros sont masqués dans les listes
              comme à l'accueil, et chaque action sensible est contrôlée côté serveur. Une recherche
              à l'accueil n'enregistre aucune présence : seule la confirmation explicite de l'agent
              le fait.
            </p>
          </section>
          <section id="droits">
            <h2>Vos droits et contact</h2>
            <p>
              Pour demander l'accès, la rectification ou la suppression de vos données, contactez
              l'organisateur indiqué dans votre invitation. Certaines traces peuvent devoir être
              conservées temporairement pour l'intégrité et la sécurité de l'événement.
            </p>
          </section>
          <p className="legal-updated">
            <Check /> Notice mise à jour le 3 septembre 2026.
          </p>
        </div>
      </article>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main id="contenu" className="not-found">
      <span>404</span>
      <p className="eyebrow">Page introuvable</p>
      <h1>Cette page ne fait pas partie de l'histoire.</h1>
      <p>Le lien est peut-être incomplet ou n'est plus disponible.</p>
      <Link className="button button--primary" to="/">
        Revenir à l'accueil
      </Link>
    </main>
  );
}
